/**
 * DTC Platform Card Component
 * 
 * Visual card for overview dashboard showing DTC platform summary.
 * Clicking card drills down to platform-specific detail page.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShoppingCart, Package, DollarSign, AlertCircle } from 'lucide-react';
import { useLocation } from 'wouter';

// Platform brand colors
const PLATFORM_COLORS = {
  shopify: {
    primary: '#95BF47',
    light: '#F0F7E8',
    medium: '#C5E09B',
    dark: '#5F7A2E'
  },
  dtc_shopify: {
    primary: '#95BF47',
    light: '#F0F7E8',
    medium: '#C5E09B',
    dark: '#5F7A2E'
  },
  amazon: {
    primary: '#FF9900',
    light: '#FFF3E0',
    medium: '#FFD699',
    dark: '#CC7A00'
  },
  dtc_amazon: {
    primary: '#FF9900',
    light: '#FFF3E0',
    medium: '#FFD699',
    dark: '#CC7A00'
  },
  amazon_seller_central: {
    primary: '#FF9900',
    light: '#FFF3E0',
    medium: '#FFD699',
    dark: '#CC7A00'
  },
  walmart: {
    primary: '#0071CE',
    light: '#E3F2FD',
    medium: '#90CAF9',
    dark: '#005BA3'
  },
  dtc_walmart: {
    primary: '#0071CE',
    light: '#E3F2FD',
    medium: '#90CAF9',
    dark: '#005BA3'
  }
};

interface DtcPlatformCardProps {
  platformId: string;
  platformName: string;
  platformType: string; // 'shopify', 'amazon', 'walmart'
  revenue: number;
  orders: number;
  avgOrderValue: number;
  units?: number;
  percentageOfTotal?: number;
  // Comparison data
  revenue_growth_prev?: number | null;
  revenue_growth_yoy?: number | null;
  orders_growth_prev?: number | null;
  orders_growth_yoy?: number | null;
  aov_growth_prev?: number | null;
  aov_growth_yoy?: number | null;
  units_growth_prev?: number | null;
  units_growth_yoy?: number | null;
  className?: string;
}

export function DtcPlatformCard({
  platformId,
  platformName,
  platformType,
  revenue,
  orders,
  avgOrderValue,
  units = 0,
  percentageOfTotal,
  revenue_growth_prev,
  revenue_growth_yoy,
  orders_growth_prev,
  orders_growth_yoy,
  aov_growth_prev,
  aov_growth_yoy,
  units_growth_prev,
  units_growth_yoy,
  className = ''
}: DtcPlatformCardProps) {
  const [, setLocation] = useLocation();
  
  const colors = PLATFORM_COLORS[platformType as keyof typeof PLATFORM_COLORS] || {
    primary: '#8b5cf6',
    light: '#f3e8ff',
    medium: '#d8b4fe',
    dark: '#6d28d9'
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };
  
  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)}%`;
  };
  
  const formatCompactChange = (growthPrev: number | null | undefined, growthYoy: number | null | undefined) => {
    const hasPrev = growthPrev !== null && growthPrev !== undefined;
    const hasYoy = growthYoy !== null && growthYoy !== undefined;
    
    if (!hasPrev && !hasYoy) {
      return <span className="text-xs text-gray-400">-</span>;
    }
    
    const growth = hasPrev ? growthPrev : growthYoy;
    const isPositive = growth! >= 0;
    
    return (
      <span className={`text-xs font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? '↑' : '↓'} {formatPercentage(growth!)}
      </span>
    );
  };

  const handleClick = () => {
    // Navigate to platform-specific page
    if (platformType === 'shopify') {
      setLocation('/dtc/overview');
    } else if (platformType === 'amazon') {
      setLocation('/dtc/amazon');
    } else if (platformType === 'walmart') {
      setLocation('/dtc/walmart');
    }
  };

  const hasData = revenue > 0 || orders > 0;

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      whileHover={hasData ? { scale: 1.02, transition: { duration: 0.2 } } : {}}
      onClick={handleClick}
      style={{
        borderColor: hasData ? colors.primary : '#E5E7EB',
        borderWidth: '2px',
        backgroundColor: hasData ? colors.light : '#ffffff'
      }}
      className={`
        group relative overflow-hidden
        rounded-xl 
        p-6 transition-shadow duration-300
        ${hasData 
          ? 'cursor-pointer hover:shadow-xl' 
          : 'cursor-not-allowed opacity-60'
        }
        ${className}
      `}
    >
      {/* Platform Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center border-2"
            style={{ 
              borderColor: colors.medium,
              backgroundColor: colors.light 
            }}
          >
            <ShoppingCart className="w-7 h-7" style={{ color: colors.primary }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {platformName}
            </h3>
            {!hasData && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                No data available
              </p>
            )}
          </div>
        </div>
        
        {/* Arrow icon (appears on hover if has data) */}
        {hasData && (
          <ArrowRight 
            className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-all"
            style={{ color: colors.primary }}
          />
        )}
      </div>

      {/* Revenue - Main Metric */}
      <div className="mb-5">
        <div className="text-3xl font-bold text-gray-900 mb-1">
          {formatCurrency(revenue)}
        </div>
        <p className="text-xs text-gray-600">Total Revenue</p>
        {percentageOfTotal !== undefined && percentageOfTotal > 0 && (
          <div 
            className="text-xs font-semibold mt-1 px-2 py-1 rounded inline-block"
            style={{ 
              backgroundColor: colors.light,
              color: colors.dark 
            }}
          >
            {percentageOfTotal.toFixed(0)}% of DTC
          </div>
        )}
      </div>

      {/* Stats Grid with Comparisons */}
      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-200">
        <div>
          <div className="flex items-center space-x-1 mb-1">
            <ShoppingCart className="w-3.5 h-3.5 text-gray-400" />
          </div>
          <div className="text-lg font-semibold text-gray-900">
            {formatNumber(orders)}
          </div>
          <div className="text-xs text-gray-600">Orders</div>
          {formatCompactChange(orders_growth_prev, orders_growth_yoy)}
        </div>
        <div>
          <div className="flex items-center space-x-1 mb-1">
            <DollarSign className="w-3.5 h-3.5 text-gray-400" />
          </div>
          <div className="text-lg font-semibold text-gray-900">
            {formatCurrency(avgOrderValue)}
          </div>
          <div className="text-xs text-gray-600">AOV</div>
          {formatCompactChange(aov_growth_prev, aov_growth_yoy)}
        </div>
        <div>
          <div className="flex items-center space-x-1 mb-1">
            <Package className="w-3.5 h-3.5 text-gray-400" />
          </div>
          <div className="text-lg font-semibold text-gray-900">
            {units > 0 ? formatNumber(units) : '-'}
          </div>
          <div className="text-xs text-gray-600">Units</div>
          {units > 0 && formatCompactChange(units_growth_prev, units_growth_yoy)}
        </div>
      </div>

      {/* Hover overlay with platform color */}
      {hasData && (
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primary}03 100%)`
          }}
        />
      )}

      {/* No data overlay message */}
      {!hasData && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-xl">
          <div className="text-center p-4">
            <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 font-medium">No Data Available</p>
            <p className="text-xs text-gray-500 mt-1">Connect platform to view insights</p>
          </div>
        </div>
      )}
    </motion.div>
  );
}

